import React, { useEffect } from 'react';

/**
 * SlideOverlay — calque pédagogique animé du player LMS ITRIIA.
 * Port React FIDÈLE de la maquette validée `calque-C.html` (direction
 * « Blueprint / tableau qui se dessine »).
 *
 * CONTRAT (le player s'y branche) :
 *   <SlideOverlay
 *      overlay={ { id, type, payload, anim, presenter } | null }
 *      presenter={ { name, role, avatar } }
 *      isDark={bool}
 *      scale={'full' | 'panel'}
 *      formationLabel="ARGUMENTAIRE & TECHNIQUES COMMERCIALES"
 *      seq={ { index, total } }
 *      medallion={ <video .../> }   // nœud React du flux vidéo (on l'ACCUEILLE)
 *   />
 *
 * - overlay.type : 'titre' | 'bullets' | 'svg' | 'stat' | 'citation'
 * - overlay.presenter : 'medallion' | 'hidden' | 'audio'  (état du présentateur)
 * - overlay === null  -> rend seulement le cadre (médaillon + footer + badge).
 *
 * GARANTIES (identiques à la maquette, pilotées par variables CSS) :
 *  - keep-out médaillon (colonne droite, centré vertical) + footer (--footer-h) ;
 *  - schémas SVG confinés (foreignObject) qui se dessinent (stroke-dashoffset) ;
 *  - 3 états présentateur ; dark/light via data-theme.
 *
 * SCOPING CSS : tout le CSS de calque-C est injecté via UNE balise <style>,
 * chaque sélecteur préfixé par `.itriia-overlay`. `:root[data-theme]` devient
 * `.itriia-overlay[data-theme]`, `.stage` devient `.itriia-overlay`, etc.
 * -> zéro fuite globale (pas de reset *, pas de styles sur body).
 */

/* ---- onde oscilloscope du badge audio (motif répété sur 40u viewBox) ---- */
const SCOPE_WAVE =
  'M-40 15 L -36 8 L -32 22 L -28 13 L -24 18 L -20 5 L -16 24 L -12 12 L -8 16 L -4 9 L 0 15 ' +
  'L 4 8 L 8 22 L 12 13 L 16 18 L 20 5 L 24 24 L 28 12 L 32 16 L 36 9 L 40 15 ' +
  'L 44 8 L 48 22 L 52 13 L 56 18 L 60 5 L 64 24 L 68 12 L 72 16 L 76 9 L 80 15 ' +
  'L 84 8 L 88 22 L 92 13 L 96 18 L 100 5 L 104 24 L 108 12 L 112 16 L 116 9 L 120 15';

/* helper : style objet avec custom-prop (--del / --i / --len / --dur) */
const sv = (obj) => obj;

/* =========================================================================
 *  CSS scopé — copie fidèle de calque-C.html, préfixé `.itriia-overlay`.
 * ========================================================================= */
const CSS = `
.itriia-overlay[data-theme="dark"]{
  --bg:#0c1620; --bg-2:#0a1018;
  --grid:rgba(110,168,178,.10); --grid-strong:rgba(120,182,194,.20);
  --ink:#E9EEF0; --ink-soft:#A9BAC0; --ink-faint:#6E8189;
  --teal:#39C3B2; --teal-deep:#0E9C8B;
  --gold:#E0BC5A; --clay:#D9743F;
  --node-fill:rgba(16,32,40,.72); --node-stroke:rgba(120,182,194,.34);
  --scrim:rgba(8,16,22,.50); --halo:rgba(8,16,22,.62);
  --line:rgba(120,182,194,.22); --draw:#5FD6C6;
  --medallion-a:#123640; --medallion-b:#0a1c22;
  --chip:rgba(57,195,178,.12); --chip-ink:#7FE0D3;
  --shadow:0 24px 60px -28px rgba(0,0,0,.7);
}
.itriia-overlay[data-theme="light"]{
  --bg:#EAF1F2; --bg-2:#DCE8EA;
  --grid:rgba(14,107,96,.10); --grid-strong:rgba(14,107,96,.18);
  --ink:#1C2A2A; --ink-soft:#4A5C5C; --ink-faint:#8AA0A0;
  --teal:#0E9C8B; --teal-deep:#0A6A60;
  --gold:#A9832F; --clay:#B7552C;
  --node-fill:rgba(255,255,255,.86); --node-stroke:rgba(14,107,96,.30);
  --scrim:rgba(244,239,229,.42); --halo:rgba(252,250,244,.78);
  --line:rgba(14,107,96,.22); --draw:#0A6A60;
  --medallion-a:#cfe2df; --medallion-b:#a9c8c2;
  --chip:rgba(14,156,139,.14); --chip-ink:#0A6A60;
  --shadow:0 24px 56px -30px rgba(33,28,22,.42);
}

/* ---- racine = ancien .stage : conteneur 16:9 + variables keep-out/footer ---- */
.itriia-overlay{
  position:relative;width:100%;aspect-ratio:16/9;border-radius:18px;overflow:hidden;
  background:var(--bg-2); border:1px solid var(--line); box-shadow:var(--shadow);
  isolation:isolate; color:var(--ink);
  font-family:'Archivo','Helvetica Neue',system-ui,sans-serif;
  -webkit-font-smoothing:antialiased;
  --med-w:22; --med-ratio:1.25; --med-margin:3; --med-gutter:2;
  --footer-h:9%;
  /* médaillon CENTRÉ VERTICALEMENT dans la colonne droite (--med-center, %H) */
  --med-center:45;
  --med-top:calc((var(--med-center) - var(--med-w) * var(--med-ratio) * 1.7778 / 2) * 1%);
  /* keep-out = BANDE colonne droite à la hauteur du médaillon (pas le coin bas) */
  --safe-w:calc((var(--med-w) + var(--med-margin) + var(--med-gutter)) * 1%);
  --safe-top:calc((var(--med-center) - (var(--med-w) * var(--med-ratio) * 1.7778)/2 - var(--med-gutter)) * 1%);
  --safe-bot:calc((var(--med-center) + (var(--med-w) * var(--med-ratio) * 1.7778)/2 + var(--med-gutter)) * 1%);
  --safe-h:calc(var(--safe-bot) - var(--safe-top));
}
.itriia-overlay *{box-sizing:border-box}
.itriia-overlay[data-presenter="hidden"], .itriia-overlay[data-presenter="audio"]{ --safe-w:0%; --safe-top:0%; --safe-bot:0%; --safe-h:0%; }

.itriia-overlay .video-floor{position:absolute;inset:0;z-index:0;
  background:
    radial-gradient(60% 80% at 22% 30%, color-mix(in srgb, var(--clay) 22%, transparent), transparent 60%),
    radial-gradient(70% 90% at 82% 76%, color-mix(in srgb, var(--teal) 26%, transparent), transparent 62%),
    linear-gradient(120deg, var(--bg) 0%, var(--bg-2) 100%);
  filter:saturate(1.05);}
.itriia-overlay .blueprint-grid{position:absolute;inset:0;z-index:1;pointer-events:none;
  background-image:
    linear-gradient(var(--grid) 1px, transparent 1px),
    linear-gradient(90deg, var(--grid) 1px, transparent 1px),
    linear-gradient(var(--grid-strong) 1px, transparent 1px),
    linear-gradient(90deg, var(--grid-strong) 1px, transparent 1px);
  background-size:28px 28px, 28px 28px, 140px 140px, 140px 140px;
  -webkit-mask-image:radial-gradient(130% 110% at 50% 40%, #000 60%, transparent 100%);
          mask-image:radial-gradient(130% 110% at 50% 40%, #000 60%, transparent 100%);}
.itriia-overlay .scrim{position:absolute;inset:0;z-index:2;pointer-events:none;
  background:linear-gradient(180deg, var(--scrim), transparent 28%, transparent 70%, var(--scrim));}

.itriia-overlay .layer{position:absolute;inset:0;z-index:3;display:none}
.itriia-overlay .layer.active{display:block}

.itriia-overlay .corner{position:absolute;width:30px;height:30px;z-index:4;color:var(--ink-faint);opacity:.5;pointer-events:none}
.itriia-overlay .corner svg{width:100%;height:100%}
.itriia-overlay .corner.tl{top:14px;left:14px}
.itriia-overlay .corner.tr{top:14px;right:14px;transform:scaleX(-1)}
.itriia-overlay .corner.bl{bottom:14px;left:14px;transform:scaleY(-1)}
.itriia-overlay .corner.br{bottom:14px;right:14px;transform:scale(-1)}

/* signature ITRIIA Studio (haut-droite) */
.itriia-overlay .studio-sig{position:absolute;z-index:5;top:14px;right:48px;
  display:flex;align-items:center;gap:8px;padding:5px 10px 5px 7px;border-radius:999px;
  border:1px solid var(--line);background:linear-gradient(180deg, var(--halo), transparent);
  color:var(--ink-faint);opacity:.62;transition:opacity .35s ease;pointer-events:none;backdrop-filter:blur(2px);}
.itriia-overlay .studio-sig .mono{flex:none;width:17px;height:17px;color:var(--teal)}
.itriia-overlay .studio-sig .mono circle,.itriia-overlay .studio-sig .mono path{vector-effect:non-scaling-stroke}
.itriia-overlay .studio-sig .txt{font-family:'Spline Sans Mono',monospace;font-size:9.5px;letter-spacing:.2em;
  text-transform:uppercase;font-weight:500;white-space:nowrap;line-height:1;}
.itriia-overlay .studio-sig .txt b{color:var(--ink-soft);font-weight:600;letter-spacing:.16em}
.itriia-overlay .studio-sig .pulse{flex:none;width:5px;height:5px;border-radius:50%;background:var(--teal);
  box-shadow:0 0 0 0 var(--chip);animation:itriiaSigPulse 3.2s ease-in-out infinite;}
@keyframes itriiaSigPulse{0%,100%{opacity:.5;box-shadow:0 0 0 0 var(--chip)}50%{opacity:1;box-shadow:0 0 0 4px transparent}}
.itriia-overlay[data-scale="panel"] .studio-sig{right:40px;top:10px;padding:3px 8px 3px 5px}
.itriia-overlay[data-scale="panel"] .studio-sig .txt{font-size:8px;letter-spacing:.14em}

/* bandeau footer (zone réservée fixe du lecteur) */
.itriia-overlay .idband{position:absolute;left:0;right:0;bottom:0;z-index:5;
  display:flex;align-items:center;gap:14px;padding:11px 26px;
  font-family:'Spline Sans Mono',monospace;font-size:10.5px;letter-spacing:.26em;
  text-transform:uppercase;color:var(--ink-faint);background:linear-gradient(0deg, var(--halo), transparent);}
.itriia-overlay .idband .dot{width:6px;height:6px;border-radius:50%;background:var(--teal);box-shadow:0 0 0 4px var(--chip)}
.itriia-overlay .idband b{color:var(--ink-soft);font-weight:600}
.itriia-overlay .idband .seq{margin-left:auto;color:var(--teal);font-weight:600}
.itriia-overlay[data-scale="panel"] .idband{padding:8px 14px;font-size:9px;letter-spacing:.18em}

/* médaillon (slot Pierre) — colonne DROITE, CENTRÉ verticalement (--med-top) */
.itriia-overlay .medallion{position:absolute;z-index:6;
  right:calc(var(--med-margin) * 1%);top:var(--med-top);
  width:calc(var(--med-w) * 1%);aspect-ratio:4/5;border-radius:20px;overflow:hidden;
  border:1px solid var(--node-stroke);background:linear-gradient(160deg, var(--medallion-a), var(--medallion-b));
  box-shadow:0 18px 40px -18px rgba(0,0,0,.6), inset 0 0 0 1px rgba(255,255,255,.04);
  transition:opacity .5s ease, transform .5s ease;}
.itriia-overlay .medallion > *{width:100%;height:100%;object-fit:cover;display:block}
.itriia-overlay[data-presenter="hidden"] .medallion,
.itriia-overlay[data-presenter="audio"] .medallion{opacity:0;transform:translateY(8px) scale(.96);pointer-events:none;}

/* badge audio (avatar + nom/rôle + onde) */
.itriia-overlay .audiobadge{position:absolute;z-index:6;
  right:calc(var(--med-margin) * 1%);bottom:calc(var(--footer-h) + 2%);
  width:clamp(186px, 23%, 322px);display:flex;align-items:center;gap:10px;
  padding:9px 13px 9px 9px;border-radius:16px;border:1px solid var(--node-stroke);
  background:linear-gradient(160deg, var(--medallion-a), var(--medallion-b));
  box-shadow:0 14px 30px -16px rgba(0,0,0,.55);
  opacity:0;transform:translateY(8px) scale(.97);pointer-events:none;transition:opacity .5s ease, transform .5s ease;}
.itriia-overlay[data-presenter="audio"] .audiobadge{opacity:1;transform:none}
.itriia-overlay .audiobadge .av-avatar{flex:none;position:relative;width:34px;height:34px;border-radius:50%;
  overflow:hidden;border:1.5px solid var(--node-stroke);
  background:
    radial-gradient(120% 90% at 50% 16%, color-mix(in srgb,var(--teal) 36%, transparent), transparent 70%),
    linear-gradient(160deg, var(--medallion-a), var(--medallion-b));
  background-size:cover;background-position:center;display:flex;align-items:center;justify-content:center;
  box-shadow:inset 0 0 0 1px color-mix(in srgb,var(--ink) 8%, transparent);}
.itriia-overlay .audiobadge .av-avatar .ini{font-family:'Fraunces',serif;font-weight:600;font-size:16px;color:var(--ink);line-height:1;opacity:.92;}
.itriia-overlay .audiobadge .av-avatar::after{content:"";position:absolute;inset:-4px;border-radius:50%;
  border:1.5px solid var(--teal);opacity:0;animation:itriiaAvRing 1.8s ease-in-out infinite;}
@keyframes itriiaAvRing{0%,100%{opacity:0;transform:scale(.86)}50%{opacity:.5;transform:scale(1)}}
.itriia-overlay .audiobadge .av-id{display:flex;flex-direction:column;gap:2px;min-width:0}
.itriia-overlay .audiobadge .av-name{font-family:'Spline Sans Mono',monospace;font-size:10px;letter-spacing:.14em;
  text-transform:uppercase;font-weight:600;color:var(--ink);white-space:nowrap;line-height:1.1;}
.itriia-overlay .audiobadge .av-role{font-family:'Spline Sans Mono',monospace;font-size:8px;letter-spacing:.2em;
  text-transform:uppercase;color:var(--ink-faint);white-space:nowrap;line-height:1;display:flex;align-items:center;gap:5px;}
.itriia-overlay .audiobadge .av-dot{flex:none;width:5px;height:5px;border-radius:50%;background:var(--teal);
  box-shadow:0 0 0 3px var(--chip);animation:itriiaAvPulse 1.8s ease-in-out infinite;}
@keyframes itriiaAvPulse{0%,100%{opacity:.55;transform:scale(.85)}50%{opacity:1;transform:scale(1.15)}}
.itriia-overlay .audiobadge .scope{flex:1;height:28px;min-width:30px}
.itriia-overlay .audiobadge .scope path{fill:none;stroke:var(--teal);stroke-width:2;stroke-linecap:round;stroke-linejoin:round;}
.itriia-overlay .audiobadge .scope .wave{animation:itriiaScopeShift 1.4s linear infinite}
@keyframes itriiaScopeShift{from{transform:translateX(0)}to{transform:translateX(-40px)}}
.itriia-overlay[data-scale="panel"] .audiobadge{width:46%;padding:6px 9px 6px 6px;gap:7px}
.itriia-overlay[data-scale="panel"] .audiobadge .av-avatar{width:26px;height:26px}
.itriia-overlay[data-scale="panel"] .audiobadge .av-avatar .ini{font-size:13px}
.itriia-overlay[data-scale="panel"] .audiobadge .av-name{font-size:9px}
.itriia-overlay[data-scale="panel"] .audiobadge .av-role{font-size:7.5px}

/* ---- TYPO SLIDE (.pad réserve médaillon + footer) ---- */
.itriia-overlay .pad{position:absolute;inset:0;display:flex;flex-direction:column;
  padding:6.2% 6%;padding-right:max(6%, var(--safe-w));
  padding-bottom:calc(1.2% + var(--footer-h) * 0.5625);transition:padding-right .5s ease, padding-bottom .4s ease;}
.itriia-overlay .pad [data-lane="low"]{max-width:100%;}
.itriia-overlay .pad-foot{flex:none;height:14px;pointer-events:none;}
.itriia-overlay .eyebrow{font-family:'Spline Sans Mono',monospace;font-size:clamp(10px,1vw,12px);
  letter-spacing:.3em;text-transform:uppercase;color:var(--teal);font-weight:600;display:inline-flex;align-items:center;gap:10px;}
.itriia-overlay .eyebrow::before{content:"";width:30px;height:2px;background:var(--teal);display:inline-block}
.itriia-overlay .h-title{font-family:'Fraunces',Georgia,serif;font-weight:900;letter-spacing:-.022em;
  font-size:clamp(28px,4.6vw,54px);line-height:1.0;margin:.4em 0 0;color:var(--ink);max-width:18ch;}
.itriia-overlay .h-title em{font-style:italic;font-weight:500;color:var(--teal)}
.itriia-overlay .h-sub{margin:.7em 0 0;color:var(--ink-soft);font-size:clamp(13px,1.4vw,17px);max-width:52ch;line-height:1.45}
.itriia-overlay .haloblock{background:radial-gradient(120% 130% at 0% 0%, var(--halo), transparent 72%);
  padding:18px 22px;margin:-18px -22px;border-radius:16px;}

.itriia-overlay .bullets{list-style:none;margin:1.4em 0 0;padding:0;display:flex;flex-direction:column;gap:.85em;max-width:60ch}
.itriia-overlay .bullets li{display:flex;gap:14px;align-items:flex-start;color:var(--ink);font-size:clamp(14px,1.7vw,20px);line-height:1.32;}
.itriia-overlay .bullets li .mk{flex:none;width:22px;height:22px;margin-top:2px;color:var(--teal);}
.itriia-overlay .bullets li b{font-weight:600}
.itriia-overlay .bullets li small{display:block;color:var(--ink-soft);font-size:.78em;margin-top:2px;font-weight:400}

.itriia-overlay .stat-wrap{margin-top:auto;display:flex;align-items:flex-end;gap:clamp(16px,3vw,40px);flex-wrap:wrap}
.itriia-overlay .statnum{font-family:'Fraunces',serif;font-weight:900;line-height:.82;letter-spacing:-.03em;
  font-size:clamp(70px,16vw,190px);color:var(--gold);position:relative;}
.itriia-overlay .statnum .unit{font-size:.42em;color:var(--ink);font-weight:600;letter-spacing:-.01em}
.itriia-overlay .stat-side{max-width:30ch;padding-bottom:.6em}
.itriia-overlay .stat-side .lbl{font-family:'Fraunces',serif;font-style:italic;font-size:clamp(16px,2vw,24px);color:var(--ink)}
.itriia-overlay .stat-side .desc{margin-top:.4em;color:var(--ink-soft);font-size:clamp(12px,1.4vw,16px);line-height:1.4}
.itriia-overlay .stat-rule{height:2px;background:var(--gold);transform-origin:left;margin-top:14px}

.itriia-overlay .quote{margin:auto 0;max-width:32ch}
.itriia-overlay .quote .glyph{font-family:'Fraunces',serif;font-size:clamp(54px,9vw,104px);line-height:.5;color:var(--teal);opacity:.5;height:.5em;display:block}
.itriia-overlay .quote q{font-family:'Fraunces',serif;font-style:italic;font-weight:500;quotes:none;
  font-size:clamp(18px,2.3vw,30px);line-height:1.22;color:var(--ink);display:block;margin-top:.1em;}
.itriia-overlay .quote q strong{color:var(--teal);font-weight:600;font-style:italic}
.itriia-overlay .quote .src{display:flex;align-items:center;gap:12px;margin-top:1.1em;
  font-family:'Spline Sans Mono',monospace;font-size:12px;letter-spacing:.2em;text-transform:uppercase;color:var(--ink-faint);}
.itriia-overlay .quote .src::before{content:"";width:40px;height:1px;background:var(--ink-faint)}

/* ---- SVG schema + foreignObject (confinement texte) ---- */
.itriia-overlay .diagram{position:absolute;inset:0;width:100%;height:100%}
.itriia-overlay .diagram text{font-family:'Spline Sans Mono',monospace}
.itriia-overlay .fo{overflow:visible}
.itriia-overlay .fo-card{box-sizing:border-box;width:100%;height:100%;
  padding:18px 22px;display:flex;flex-direction:column;justify-content:center;gap:2px;
  font-family:'Archivo',sans-serif;overflow:hidden;}
.itriia-overlay .fo-card .kick{font-family:'Spline Sans Mono',monospace;font-size:12.5px;letter-spacing:2.5px;
  text-transform:uppercase;font-weight:600;line-height:1.1;margin-bottom:2px;}
.itriia-overlay .fo-card .ttl{font-family:'Fraunces',serif;font-weight:700;line-height:1.04;color:var(--ink)}
.itriia-overlay .fo-card .row{display:flex;align-items:baseline;gap:8px}
.itriia-overlay .fo-card .body{font-size:14.5px;line-height:1.3;color:var(--ink-soft);overflow-wrap:break-word;word-break:normal;hyphens:auto}
.itriia-overlay .fo-card .body.faint{color:var(--ink-faint);font-style:italic;font-size:13.5px}
.itriia-overlay .fo-card .ico{flex:none;width:30px;height:24px;margin-bottom:4px}
.itriia-overlay .fo-card.center{align-items:center;text-align:center;justify-content:center}
.itriia-overlay .fo-card.pill{padding:6px 18px}

.itriia-overlay .draw{stroke-dasharray:var(--len,900);stroke-dashoffset:var(--len,900);}
.itriia-overlay .node-card{opacity:0;transform-box:fill-box;transform-origin:center;transform:scale(.9)}
.itriia-overlay .fade{opacity:0}
.itriia-overlay .zone-free{display:none}
.itriia-overlay[data-presenter="hidden"] .zone-free,
.itriia-overlay[data-presenter="audio"] .zone-free{display:block}

/* ---- moteur d'animation (entrée) ---- */
.itriia-overlay .layer.active .ci-up{animation:itriiaUpIn .6s cubic-bezier(.2,.7,.2,1) both}
.itriia-overlay .layer.active .ci-up.d1{animation-delay:.06s}
.itriia-overlay .layer.active .ci-up.d2{animation-delay:.16s}
.itriia-overlay .layer.active .ci-up.d3{animation-delay:.28s}
@keyframes itriiaUpIn{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:none}}
.itriia-overlay .layer.active .bullets li{animation:itriiaBul .5s cubic-bezier(.2,.7,.2,1) both;animation-delay:calc(.12s * var(--i) + .15s)}
@keyframes itriiaBul{from{opacity:0;transform:translateX(-16px)}to{opacity:1;transform:none}}
.itriia-overlay .layer.active .bullets li .mk path,
.itriia-overlay .layer.active .bullets li .mk line,
.itriia-overlay .layer.active .bullets li .mk circle{stroke-dasharray:30;stroke-dashoffset:30;
  animation:itriiaTrace .5s ease forwards;animation-delay:calc(.12s * var(--i) + .25s)}
.itriia-overlay .layer.active .draw{animation:itriiaTrace var(--dur,.7s) ease forwards;animation-delay:var(--del,0s)}
@keyframes itriiaTrace{to{stroke-dashoffset:0}}
.itriia-overlay .layer.active .node-card{animation:itriiaPop .55s cubic-bezier(.2,.8,.2,1) forwards;animation-delay:var(--del,0s)}
@keyframes itriiaPop{to{opacity:1;transform:scale(1)}}
.itriia-overlay .layer.active .fade{animation:itriiaFin .5s ease forwards;animation-delay:var(--del,0s)}
@keyframes itriiaFin{to{opacity:1}}
.itriia-overlay .layer.active .statnum{animation:itriiaUpIn .6s cubic-bezier(.2,.7,.2,1) both .1s}
.itriia-overlay .layer.active .stat-rule{animation:itriiaWipe .6s cubic-bezier(.5,0,.1,1) both .5s}
@keyframes itriiaWipe{from{transform:scaleX(0)}to{transform:scaleX(1)}}
.itriia-overlay .layer.active .tension{animation:itriiaTension 2.4s ease-in-out 1.7s infinite}
@keyframes itriiaTension{0%,100%{stroke-width:2;opacity:.85}50%{stroke-width:3.4;opacity:1}}

/* ---- sortie (cue-out) ---- */
.itriia-overlay .layer.leaving{display:block;animation:none}
.itriia-overlay .layer.leaving .pad,
.itriia-overlay .layer.leaving .diagram{animation:itriiaCueOut .5s cubic-bezier(.6,0,.2,1) both}
@keyframes itriiaCueOut{to{opacity:0;transform:translateY(-12px) scale(.985)}}

@media (prefers-reduced-motion:reduce){
  .itriia-overlay .studio-sig .pulse,
  .itriia-overlay .audiobadge .scope .wave,
  .itriia-overlay .audiobadge .av-dot,
  .itriia-overlay .audiobadge .av-avatar::after{animation:none}
}
`;

/* injecte le CSS scopé UNE seule fois pour tout le document */
let cssInjected = false;
function useScopedCss() {
  useEffect(() => {
    if (cssInjected) return;
    const el = document.createElement('style');
    el.setAttribute('data-itriia-overlay', '');
    el.textContent = CSS;
    document.head.appendChild(el);
    cssInjected = true;
  }, []);
}

/* ============================ CHROME COMMUN ============================== */
const Corner = ({ pos }) => (
  <div className={`corner ${pos}`}>
    <svg viewBox="0 0 30 30" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M2 12V2h10" /></svg>
  </div>
);

function StudioSig() {
  return (
    <div className="studio-sig" aria-label="Signé ITRIIA Studio">
      <svg className="mono" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="10" cy="10" r="7.4" stroke="currentColor" strokeWidth="1.3" />
        <circle cx="10" cy="6.4" r="1.5" fill="currentColor" />
        <path d="M10 9.4 V 14.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
      <span className="txt"><b>ITRIIA</b> Studio</span>
      <span className="pulse" />
    </div>
  );
}

function AudioBadge({ presenter }) {
  const name = presenter?.name || 'Présentateur';
  const role = presenter?.role || 'Voix';
  const initial = (name.trim()[0] || 'P').toUpperCase();
  const avatarStyle = presenter?.avatar ? { backgroundImage: `url(${presenter.avatar})` } : undefined;
  return (
    <div className="audiobadge">
      <span className="av-avatar" role="img" aria-label={name} style={avatarStyle}>
        {!presenter?.avatar && <span className="ini">{initial}</span>}
      </span>
      <span className="av-id">
        <span className="av-name">{name}</span>
        <span className="av-role"><span className="av-dot" />{role}</span>
      </span>
      <svg className="scope" viewBox="0 0 80 30" preserveAspectRatio="none" aria-hidden="true">
        <path className="wave" d={SCOPE_WAVE} />
      </svg>
    </div>
  );
}

/* ====================== CONTENU PAR TYPE DE CUE ========================= */
const ICON_CHECK = (
  <svg className="mk" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="4" /><path d="M8 12l3 3 5-6" />
  </svg>
);

function TitreSlide({ payload }) {
  const { kicker, title } = payload || {};
  return (
    <div className="pad">
      <div className="haloblock" style={{ marginBottom: 'auto' }}>
        {kicker && <span className="eyebrow ci-up d1">{kicker}</span>}
        {title && <h1 className="h-title ci-up d2" dangerouslySetInnerHTML={{ __html: emph(title) }} />}
      </div>
    </div>
  );
}

function BulletsSlide({ payload }) {
  const { kicker, title, items = [] } = payload || {};
  return (
    <div className="pad">
      {(kicker || title) && (
        <div className="haloblock">
          {kicker && <span className="eyebrow ci-up d1">{kicker}</span>}
          {title && <h1 className="h-title ci-up d2" style={{ fontSize: 'clamp(24px,3.6vw,40px)' }} dangerouslySetInnerHTML={{ __html: emph(title) }} />}
        </div>
      )}
      <ul className="bullets" data-lane="low">
        {items.map((it, i) => (
          <li key={i} style={sv({ '--i': i })}>
            {ICON_CHECK}
            <span dangerouslySetInnerHTML={{ __html: bulletHtml(it) }} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatSlide({ payload }) {
  const { value, label, sub } = payload || {};
  const m = String(value ?? '').match(/^([^/]*?)(\/\s*\S+)?$/);
  const main = m ? m[1] : value;
  const unit = m && m[2] ? m[2].replace(/\s+/g, '') : null;
  return (
    <div className="pad">
      <div className="haloblock" style={{ marginBottom: 'auto' }}>
        <span className="eyebrow ci-up d1">Pourquoi ça compte</span>
      </div>
      <div className="stat-wrap" data-lane="low">
        <div>
          <div className="statnum">{main}{unit && <span className="unit">{unit}</span>}</div>
          <div className="stat-rule" />
        </div>
        <div className="stat-side ci-up d3">
          {label && <div className="lbl">{label}</div>}
          {sub && <div className="desc">{sub}</div>}
        </div>
      </div>
      <div className="pad-foot" />
    </div>
  );
}

function CitationSlide({ payload }) {
  const { quote, author } = payload || {};
  return (
    <div className="pad">
      <div className="quote">
        <span className="glyph ci-up d1">&ldquo;</span>
        <q className="ci-up d2" dangerouslySetInnerHTML={{ __html: emphStrong(quote || '') }} />
        {author && <div className="src ci-up d3">{author}</div>}
      </div>
    </div>
  );
}

/* ---------- SCHÉMA HÉROS « 2 PEURS » (foreignObject + traçage) ---------- */
function FoCard({ children, x, y, w, h, cls = '', style }) {
  return (
    <foreignObject className="fo" x={x} y={y} width={w} height={h}>
      <div xmlns="http://www.w3.org/1999/xhtml" className={`fo-card ${cls}`.trim()} style={style}>
        {children}
      </div>
    </foreignObject>
  );
}

function DeuxPeursSvg({ payload }) {
  const p = payload || {};
  const thesis = p.thesis || 'Un produit, deux peurs';
  const fears = p.fears && p.fears.length >= 2 ? p.fears : [
    { tag: 'Peur n°1', label: "L'IA", desc: 'Matrix, les machines froides' },
    { tag: 'Peur n°2', label: 'Le commerce', desc: 'le démarchage qui sature' },
  ];
  const reflexes = p.reflexes && p.reflexes.length >= 2 ? p.reflexes
    : ['« rassurer sur la machine »', '« ne pas vendre comme les autres »'];
  const paradox = p.paradox || 'le paradoxe';

  return (
    <svg className="diagram" viewBox="0 0 1280 720" preserveAspectRatio="xMidYMid meet"
      aria-label="Schéma : la schizophrénie de vente entre peur de l'IA et peur du commerce">
      {/* eyebrow + titre */}
      <g className="fade" style={sv({ '--del': '.05s' })}>
        <rect x="64" y="58" width="34" height="3" fill="var(--teal)" />
        <text x="110" y="65" fontSize="15" letterSpacing="4" fill="var(--teal)" fontWeight="600">CONTEXTE DE VENTE · LEÇON 1</text>
      </g>
      <text className="fade" style={sv({ '--del': '.12s' })} x="62" y="116" fontFamily="Fraunces,serif" fontSize="48" fontWeight="900" fill="var(--ink)" letterSpacing="-1">La schizophrénie</text>

      {/* connecteurs */}
      <path className="draw" style={sv({ '--len': 300, '--del': '.5s', '--dur': '.7s' })} d="M470 248 C 380 248, 320 268, 250 296" fill="none" stroke="var(--draw)" strokeWidth="2" strokeLinecap="round" />
      <path className="draw" style={sv({ '--len': 300, '--del': '.5s', '--dur': '.7s' })} d="M700 248 C 770 248, 800 268, 830 296" fill="none" stroke="var(--draw)" strokeWidth="2" strokeLinecap="round" />
      <path className="draw" style={sv({ '--len': 120, '--del': '1.5s', '--dur': '.5s' })} d="M210 460 L 210 512" fill="none" stroke="var(--node-stroke)" strokeWidth="1.6" strokeDasharray="6 6" />
      <path className="draw" style={sv({ '--len': 120, '--del': '1.5s', '--dur': '.5s' })} d="M770 460 L 770 512" fill="none" stroke="var(--node-stroke)" strokeWidth="1.6" strokeDasharray="6 6" />

      {/* tension paradoxe */}
      <path className="draw tension" style={sv({ '--len': 620, '--del': '2.5s', '--dur': '1s' })} d="M250 372 C 430 446, 640 446, 830 372" fill="none" stroke="var(--clay)" strokeWidth="2" strokeDasharray="2 9" strokeLinecap="round" />

      {/* nœud central */}
      <g className="node-card" style={sv({ '--del': '.3s' })}>
        <rect x="460" y="194" width="256" height="82" rx="12" fill="var(--node-fill)" stroke="var(--teal)" strokeWidth="1.6" />
        <rect x="460" y="194" width="6" height="82" rx="3" fill="var(--teal)" />
        <FoCard x="466" y="194" w="250" h="82">
          <span className="kick" style={{ color: 'var(--teal)' }}>La thèse</span>
          <span className="ttl" style={{ fontSize: '21px' }}>{thesis}</span>
        </FoCard>
      </g>

      {/* peur 1 */}
      <g className="node-card" style={sv({ '--del': '1.05s' })}>
        <rect x="60" y="292" width="300" height="168" rx="14" fill="var(--node-fill)" stroke="var(--clay)" strokeWidth="1.6" />
        <rect x="60" y="292" width="300" height="6" rx="3" fill="var(--clay)" />
        <FoCard x="60" y="298" w="300" h="162" style={{ justifyContent: 'flex-start', paddingTop: '16px' }}>
          <span className="ico" aria-hidden="true">
            <svg viewBox="0 0 30 24" fill="none" stroke="var(--clay)" strokeWidth="2" strokeLinecap="round"><rect x="2" y="3" width="26" height="18" rx="3" /><circle cx="10" cy="12" r="2.2" fill="var(--clay)" stroke="none" /><circle cx="20" cy="12" r="2.2" fill="var(--clay)" stroke="none" /></svg>
          </span>
          <span className="row"><span className="kick" style={{ color: 'var(--clay)' }}>{fears[0].tag}</span><span className="ttl" style={{ fontSize: '24px' }}>{fears[0].label}</span></span>
          <span className="body">{fears[0].desc}</span>
          {fears[0].note && <span className="body faint">{fears[0].note}</span>}
        </FoCard>
      </g>

      {/* peur 2 */}
      <g className="node-card" style={sv({ '--del': '1.05s' })}>
        <rect x="620" y="292" width="300" height="168" rx="14" fill="var(--node-fill)" stroke="var(--clay)" strokeWidth="1.6" />
        <rect x="620" y="292" width="300" height="6" rx="3" fill="var(--clay)" />
        <FoCard x="620" y="298" w="300" h="162" style={{ justifyContent: 'flex-start', paddingTop: '16px' }}>
          <span className="ico" aria-hidden="true">
            <svg viewBox="0 0 30 24" fill="none" stroke="var(--clay)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 9 L17 4 V20 L4 15 Z" /><path d="M22 9 q4 3 0 6" /></svg>
          </span>
          <span className="row"><span className="kick" style={{ color: 'var(--clay)' }}>{fears[1].tag}</span><span className="ttl" style={{ fontSize: '24px' }}>{fears[1].label}</span></span>
          <span className="body">{fears[1].desc}</span>
          {fears[1].note && <span className="body faint">{fears[1].note}</span>}
        </FoCard>
      </g>

      {/* badge paradoxe */}
      <g className="node-card" style={sv({ '--del': '2.7s' })}>
        <rect x="440" y="394" width="210" height="48" rx="24" fill="var(--node-fill)" stroke="var(--clay)" strokeWidth="1.6" />
        <circle cx="466" cy="418" r="7" fill="none" stroke="var(--clay)" strokeWidth="2" />
        <path d="M466 414 v5 M466 422 v.5" stroke="var(--clay)" strokeWidth="2" strokeLinecap="round" />
        <FoCard x="480" y="394" w="170" h="48" cls="pill" style={{ justifyContent: 'center' }}>
          <span className="ttl" style={{ fontStyle: 'italic', fontWeight: 600, fontSize: '18px', color: 'var(--clay)' }}>{paradox}</span>
        </FoCard>
      </g>

      {/* réflexes */}
      <g className="node-card" style={sv({ '--del': '1.9s' })}>
        <rect x="84" y="512" width="276" height="68" rx="12" fill="none" stroke="var(--teal)" strokeWidth="1.4" />
        <FoCard x="84" y="512" w="276" h="68" style={{ padding: '11px 18px', gap: '3px' }}>
          <span className="kick" style={{ color: 'var(--teal)', fontSize: '11px', letterSpacing: '2px' }}>Réflexe à désarmer</span>
          <span className="body" style={{ fontSize: '13.5px' }}>{reflexes[0]}</span>
        </FoCard>
      </g>
      <g className="node-card" style={sv({ '--del': '1.9s' })}>
        <rect x="620" y="512" width="300" height="68" rx="12" fill="none" stroke="var(--teal)" strokeWidth="1.4" />
        <FoCard x="620" y="512" w="300" h="68" style={{ padding: '11px 18px', gap: '3px' }}>
          <span className="kick" style={{ color: 'var(--teal)', fontSize: '11px', letterSpacing: '2px' }}>Réflexe à désarmer</span>
          <span className="body" style={{ fontSize: '13.5px' }}>{reflexes[1]}</span>
        </FoCard>
      </g>

      {/* phrase-clé */}
      <g className="fade" style={sv({ '--del': '3.1s' })}>
        <text x="62" y="636" fontFamily="Fraunces,serif" fontStyle="italic" fontSize="22" fill="var(--ink)">
          sauve l'humanité
          <tspan fill="var(--clay)" fontFamily="'Spline Sans Mono',monospace" fontStyle="normal" dx="8" dy="-2">⟷</tspan>
          <tspan dx="8" dy="2">lui fait peur « comme jamais »</tspan>
        </text>
      </g>

      {/* zone-free : coin libéré quand présentateur masqué/audio */}
      <g className="zone-free">
        <path className="draw" style={sv({ '--len': 120, '--del': '2.9s', '--dur': '.5s' })} d="M896 372 C 960 372, 1000 392, 1040 470" fill="none" stroke="var(--node-stroke)" strokeWidth="1.6" strokeDasharray="6 6" />
        <g className="node-card" style={sv({ '--del': '3.1s' })}>
          <rect x="968" y="470" width="266" height="150" rx="14" fill="var(--node-fill)" stroke="var(--gold)" strokeWidth="1.6" />
          <rect x="968" y="470" width="266" height="6" rx="3" fill="var(--gold)" />
          <FoCard x="968" y="476" w="266" h="144" style={{ justifyContent: 'flex-start', paddingTop: '16px' }}>
            <span className="kick" style={{ color: 'var(--gold)' }}>La sortie</span>
            <span className="ttl" style={{ fontSize: '23px' }}>Tenir les deux</span>
            <span className="body" style={{ marginTop: '4px' }}>assumer le paradoxe, ne nier aucune des 2 peurs</span>
          </FoCard>
        </g>
      </g>
    </svg>
  );
}

function SvgSlide({ payload }) {
  // schéma par défaut = héros « deux-peurs » (seul schéma livré au POC)
  return <DeuxPeursSvg payload={payload} />;
}

/* dispatcher par type */
function SlideContent({ overlay }) {
  switch (overlay.type) {
    case 'titre': return <TitreSlide payload={overlay.payload} />;
    case 'bullets': return <BulletsSlide payload={overlay.payload} />;
    case 'stat': return <StatSlide payload={overlay.payload} />;
    case 'citation': return <CitationSlide payload={overlay.payload} />;
    case 'svg': return <SvgSlide payload={overlay.payload} />;
    default: return null;
  }
}

/* ----- petits helpers de mise en forme inline (<em>, <strong>, <small>) ----- */
function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
// *texte* -> <em>, **texte** -> <strong> (sécurisé : on échappe d'abord)
function emph(s) { return esc(s).replace(/\*\*([^*]+)\*\*/g, '<em>$1</em>').replace(/\*([^*]+)\*/g, '<em>$1</em>'); }
function emphStrong(s) { return esc(s).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>').replace(/\*([^*]+)\*/g, '<strong>$1</strong>'); }
// "Titre :: sous-texte" -> <b>Titre</b><small>sous-texte</small>
function bulletHtml(it) {
  const parts = String(it).split('::');
  if (parts.length > 1) return `<b>${esc(parts[0].trim())}</b><small>${esc(parts.slice(1).join('::').trim())}</small>`;
  return `<b>${esc(it)}</b>`;
}

/* ============================ COMPOSANT ================================= */
export default function SlideOverlay({
  overlay,
  presenter,
  isDark,
  scale = 'full',
  formationLabel,
  seq,
  medallion,
}) {
  useScopedCss();

  // Ré-arme l'animation d'entrée à chaque changement de cue :
  // `key={overlay.id}` force React à REMONTER la couche -> .active est neuf
  // -> les keyframes CSS (.layer.active ...) rejouent. Idiome React, zéro JS
  // d'animation. (Le cue-out se branche au besoin via une classe .leaving.)
  const presenterMode = (overlay && overlay.presenter) || 'medallion';
  const fmtIndex = seq ? String(seq.index).padStart(2, '0') : '00';
  const fmtTotal = seq ? String(seq.total).padStart(2, '0') : '00';

  return (
    <div
      className="itriia-overlay"
      data-theme={isDark ? 'dark' : 'light'}
      data-presenter={presenterMode}
      data-scale={scale}
    >
      {/* fond + chrome */}
      <div className="video-floor" />
      <div className="blueprint-grid" />
      <div className="scrim" />
      <Corner pos="tl" /><Corner pos="tr" /><Corner pos="bl" /><Corner pos="br" />
      <StudioSig />

      {/* contenu du cue (null -> seulement le cadre) */}
      {overlay && (
        <div className="layer active" key={overlay.id}>
          <SlideContent overlay={overlay} />
        </div>
      )}

      {/* bandeau footer (zone réservée fixe du lecteur) */}
      <div className="idband">
        <span className="dot" />
        <span>FORMATION · <b>{formationLabel || 'FORMATION'}</b></span>
        <span className="seq">SÉQ. {fmtIndex} / {fmtTotal}</span>
      </div>

      {/* slot médaillon : on accueille le <video> du player */}
      <div className="medallion">{medallion}</div>

      {/* badge audio : visible via CSS quand data-presenter="audio" */}
      <AudioBadge presenter={presenter} />
    </div>
  );
}
